import type { ArraySlice, Join, Split } from "type-fest";

import type { OfferedInstrumentVersion } from "../types/configuration/catalogue.js";
import type { ManifestEntry } from "./instrument.js";
import {
  isWithinPrefix,
  parentOf,
  parseInstrumentName,
  renderInstrumentName,
} from "./name.js";

/**
 * The published versions, newest first within each prefix.
 *
 * The manifest is in publication order, so the newest is the last entry — which
 * is what "the newest version extending this prefix" is decided on rather than
 * by comparing version segments, whose meaning belongs to whoever publishes the
 * instrument and not to us.
 */
const newestFirst = (entries: readonly ManifestEntry[]): ManifestEntry[] =>
  [...entries].reverse();

/**
 * What a body may be entitled to: a published name without its last segment.
 *
 * The last segment is the revision, which corrects a transcription of the
 * instrument rather than changing what it asks — so nobody choosing what their
 * authority may use should have to choose between a typo and its fix. Every
 * other segment is theirs to choose between.
 */
export const instrumentPrefixes = (
  entries: readonly ManifestEntry[],
): string[] => [
  ...new Set(
    entries
      .map((entry) => parentOf(entry.module.name))
      .filter((prefix) => prefix !== undefined)
      .map(renderInstrumentName),
  ),
];

/**
 * The version an assessment starting under a prefix pins: the newest published
 * name that extends it.
 *
 * Nothing where the prefix names no published version — which is how a setting
 * naming an instrument this build does not carry is reported rather than
 * guessed at.
 */
export const newestWithin = (
  entries: readonly ManifestEntry[],
  prefix: string,
): ManifestEntry | undefined => {
  const parsed = parseInstrumentName(prefix);

  return parsed.success
    ? newestFirst(entries).find((entry) =>
        isWithinPrefix(entry.module.name, parsed.data),
      )
    : undefined;
};

/** One published version by its full name, or nothing where none is published under it. */
export const publishedNamed = (
  entries: readonly ManifestEntry[],
  name: string,
): ManifestEntry | undefined =>
  entries.find((entry) => renderInstrumentName(entry.module.name) === name);

/** A published name without its revision, which is what versions are compared by. */
const versionOf = (name: string): string | undefined => {
  const parsed = parseInstrumentName(name);
  const version = parsed.success ? parentOf(parsed.data) : undefined;

  return version === undefined ? undefined : renderInstrumentName(version);
};

/** A version's name without its own last segment: the instrument every version of it shares. */
type InstrumentOf<Version extends string> = Join<
  ArraySlice<Split<Version, "/">, 0, -1>,
  "/"
>;

/** An instrument a body may be offered a version of, such as `sample_cans/mini`. */
export type OfferedInstrument = InstrumentOf<OfferedInstrumentVersion>;

/**
 * What practitioners call each instrument a body may be offered, shared by
 * every version of it. Keyed by the offered versions' instruments, so offering
 * a new version of a named instrument needs nothing here, and offering a new
 * instrument does not compile until it is named.
 */
const displayNamesByInstrument: Record<OfferedInstrument, string> = {
  "sample/core": "Sample core",
  "sample_cans/comprehensive": "CANS Comprehensive",
  "sample_cans/mini": "CANS Mini",
};

const isOfferedInstrument = (
  instrument: string,
): instrument is OfferedInstrument =>
  Object.hasOwn(displayNamesByInstrument, instrument);

/** The parts of the display name of the version a published name is a revision of. */
const findDisplayNameParts = (
  name: string,
): { instrumentDisplayName: string; versionSegment: string } | undefined => {
  const parsed = parseInstrumentName(name);
  const version = parsed.success ? parentOf(parsed.data) : undefined;
  const instrument = version === undefined ? undefined : parentOf(version);
  const versionSegment = version?.at(-1);

  if (instrument === undefined || versionSegment === undefined) {
    return undefined;
  }

  const rendered = renderInstrumentName(instrument);

  return isOfferedInstrument(rendered)
    ? {
        instrumentDisplayName: displayNamesByInstrument[rendered],
        versionSegment,
      }
    : undefined;
};

/**
 * What practitioners call the instrument a published revision is of, never
 * naming its version, such as "CANS Mini" for `sample_cans/mini/1.0/r2`. Takes
 * a revision's name: a version's name without its revision finds nothing, as
 * does a revision of an instrument nobody is offered.
 */
export const findInstrumentDisplayName = (name: string): string | undefined =>
  findDisplayNameParts(name)?.instrumentDisplayName;

/**
 * What practitioners call the version a published revision is of, such as
 * "CANS Mini 1.0" for `sample_cans/mini/1.0/r2`. Takes a revision's name: a
 * version's name without its revision finds nothing, as does a revision of an
 * instrument nobody is offered.
 */
export const findInstrumentVersionDisplayName = (
  name: string,
): string | undefined => {
  const parts = findDisplayNameParts(name);

  return parts === undefined
    ? undefined
    : `${parts.instrumentDisplayName} ${parts.versionSegment}`;
};

/**
 * What to show for the version a published revision is of: what practitioners
 * call it, or, where no offered instrument contains it, the version's own name.
 * Never the revision, which nobody chooses between.
 */
export const instrumentVersionLabel = (name: string): string =>
  findInstrumentVersionDisplayName(name) ?? versionOf(name) ?? name;

/**
 * Whether `earlier` is `later` or a version it followed, through the
 * manifest's predecessors: the same instrument, at that version or an earlier
 * one. Lineage is the manifest's, so two versions are one instrument only
 * where publishing said so, whatever their names have in common.
 *
 * Compared by version, so a corrected revision of a version on the way counts
 * as that version, whichever revision a later version followed.
 */
export const isInTheLineageOf = (
  entries: readonly ManifestEntry[],
  earlier: string,
  later: string,
): boolean => {
  const version = versionOf(earlier);
  const seen = new Set<string>();
  let current: string | null = later;

  while (current !== null && !seen.has(current)) {
    if (
      current === earlier ||
      (version !== undefined && versionOf(current) === version)
    ) {
      return true;
    }

    seen.add(current);
    current = publishedNamed(entries, current)?.predecessor ?? null;
  }

  return false;
};
